// Created on 2026-02-07 by Farhan Khan - Mounting Structure Tab for Quotation
// Uses EXACT same fields and structure as MountingStructureForm.jsx
// Do NOT invent fields - all fields must match MountingStructureForm.jsx exactly
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Card,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import solisPowerApi from "../../api/solisPowerApi";
import "./QuotationMountingStructures.css";
import PubSub from "pubsub-js";
import { PREDEFINED_BOM } from "../../constants/bomPredefinedData";
import { MASTER_TYPE_MAPPING } from "../../constants/CONSTANT";
import { v4 as uuidv4 } from "uuid";
import debounce from "lodash/debounce";

// --- PERFORMANCE OPTIMIZED SUB-COMPONENTS ---
// Created on 2026-03-24 by Farhan Khan - Optimized for large tables
const EditableCell = React.memo(({ value, onChange, placeholder = "", className = "" }) => {
  const [localValue, setLocalValue] = useState(value || "");

  // Sync with prop changes (e.g., undo/redo or reset)
  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const debouncedOnChange = useRef(
    debounce((nextVal) => {
      if (onChangeRef.current) {
        onChangeRef.current(nextVal);
      }
    }, 300)
  ).current;

  // Cleanup debounce on unmount to prevent setting state on unmounted components
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const handleLocalChange = (e) => {
    const nextVal = e.target.value;
    setLocalValue(nextVal);
    debouncedOnChange(nextVal);
  };

  return (
    <Form.Control
      size="sm"
      value={localValue}
      placeholder={placeholder}
      className={className}
      onChange={handleLocalChange}
    />
  );
});

const PartRow = React.memo(({ part, sIdx, pIdx, shapeOptions, makeOptions, handlePartFieldChange, setShowAddShapePrompt, deletePartRow }) => {
  return (
    <tr>
      <td>
        <Form.Control
          size="sm"
          value={part.item_name || ""}
          className="fw-bold px-2 border-0 bg-light"
          onChange={(e) => handlePartFieldChange(sIdx, pIdx, "item_name", e.target.value)}
        />
      </td>
      <td>
        <EditableCell
          value={part.specification || ""}
          className="border-0 bg-light"
          onChange={(val) => handlePartFieldChange(sIdx, pIdx, "specification", val)}
        />
      </td>
      <td>
        <EditableCell
          value={part.size || ""}
          className="border-0 bg-light"
          onChange={(val) => handlePartFieldChange(sIdx, pIdx, "size", val)}
        />
      </td>
      <td>
        <div className="d-flex align-items-center gap-1">
          <div className="flex-fill">
            <Select
              options={shapeOptions}
              value={shapeOptions.find(o => o.value === part.shape) || null}
              onChange={(opt) => handlePartFieldChange(sIdx, pIdx, "shape", opt ? opt.value : "")}
              menuPortalTarget={document.body}
              styles={{
                control: b => ({ ...b, minHeight: "32px", fontSize: "0.75rem", border: "0", background: "#f8f9fa" }),
                option: (base) => ({
                  ...base,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  padding: "8px 12px"
                }),
                singleValue: (base) => ({
                  ...base,
                  whiteSpace: "normal"
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                  minWidth: "150px"
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
              isClearable
            />
          </div>
          <Button
            size="sm"
            variant="light"
            className="border-0 py-0 px-2 shadow-sm"
            title="Add New Shape"
            onClick={() => setShowAddShapePrompt(true)}
          >
            <i className="fa fa-plus text-primary small"></i>
          </Button>
        </div>
      </td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div className="flex-fill">
            {(() => {
              const formattedMakeOptions = Array.isArray(makeOptions) ? makeOptions : [];

              return (
                <Select
                  isMulti
                  placeholder="Select Make / Brand..."
                  value={formattedMakeOptions.filter(opt =>
                    (Array.isArray(part.make) ? part.make : []).some(m => String(m).toUpperCase() === String(opt.label).toUpperCase())
                  )}
                  onChange={(selected) => {
                    handlePartFieldChange(sIdx, pIdx, "make", selected || []);
                  }}
                  options={formattedMakeOptions}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "32px", fontSize: "0.75rem", border: "0", background: "#f8f9fa" }),
                    option: (base) => ({
                      ...base,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      padding: "8px 12px"
                    }),
                    singleValue: (base) => ({
                      ...base,
                      whiteSpace: "normal"
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#e6f0ff",
                      borderRadius: "6px",
                      padding: "2px 6px",
                      display: "flex",
                      alignItems: "center",
                      marginRight: "4px"
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#333",
                      whiteSpace: "normal"
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      cursor: "pointer"
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: "200px"
                    }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                />
              );
            })()}
          </div>
          <Button
            size="sm"
            variant="link"
            className="text-danger p-0 border-0"
            title="Delete Row"
            onClick={() => deletePartRow(sIdx, pIdx, part.id)}
          >
            <i className="fa fa-trash"></i>
          </Button>
        </div>
      </td>
    </tr>
  );
});

const OthersRow = React.memo(({ part, sIdx, pIdx, handlePartFieldChange }) => {
  return (
    <tr>
      <td colSpan="5">
        <div className="d-flex align-items-center">
          <div className="bg-light px-3 py-1 border-end small fw-bold text-muted" style={{ minWidth: "120px" }}>OTHERS</div>
          <EditableCell
            placeholder="Enter any additional requirements/notes..."
            value={(part.specification === "-" || part.specification == null) ? "" : part.specification}
            className="border-0 bg-light-subtle flex-fill"
            onChange={(val) => handlePartFieldChange(sIdx, pIdx, "specification", val)}
          />
        </div>
      </td>
    </tr>
  );
});

const ComboRow = React.memo(({ pi, sIdx, piIdx, handleComboFieldChange, deletePartRow }) => {
  return (
    <tr>
      <td className="text-center">{pi.s_no || piIdx + 1}</td>
      <td>
        <Select
          defaultValue={pi.item_name ? { value: pi.item_name, label: pi.item_name } : null}
          options={[
            { value: "RCC", label: "RCC" },
            { value: "GROUND", label: "GROUND" },
            { value: "TIN SHED", label: "TIN SHED" }
          ]}
          onChange={(opt) => handleComboFieldChange(sIdx, piIdx, "roof_type", opt ? opt.value : "")}
          menuPortalTarget={document.body}
          styles={{
            control: b => ({ ...b, minHeight: "32px", fontSize: "0.75rem", border: "0", background: "#f8f9fa" }),
            option: (base) => ({
              ...base,
              whiteSpace: "normal",
              wordBreak: "break-word",
              padding: "8px 12px"
            }),
            singleValue: (base) => ({
              ...base,
              whiteSpace: "normal"
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
              minWidth: "150px"
            }),
            menuPortal: base => ({ ...base, zIndex: 9999 })
          }}
        />
      </td>
      <td>
        <div className="d-flex align-items-center gap-2">
          <EditableCell
            type="number"
            value={pi.no_of_panels || ""}
            className="border-0 bg-light flex-fill"
            onChange={(val) => handleComboFieldChange(sIdx, piIdx, "no_of_panels", val)}
          />
          <Button
            size="sm"
            variant="link"
            className="text-danger p-0 border-0"
            title="Delete Row"
            onClick={() => deletePartRow(sIdx, piIdx, pi.id)}
          >
            <i className="fa fa-trash"></i>
          </Button>
        </div>
      </td>
    </tr>
  );
});

const QuotationMountingStructures = ({
  quotationId,
  mountingStructureItems,
  setMountingStructureItems,
  activeTab
}) => {
  const [bomCategoryOptions, setBomCategoryOptions] = useState([]);
  const [makeOptions, setMakeOptions] = useState([]);
  const [shapeOptions, setShapeOptions] = useState([
    { value: "SQUARE PIPE", label: "SQUARE PIPE" },
    { value: "CHANNEL", label: "CHANNEL" },
    { value: "C-CHANNEL", label: "C-CHANNEL" },
    { value: "CUBE", label: "CUBE" }
  ]);
  const [showAddShapePrompt, setShowAddShapePrompt] = useState(false);
  const [newShapeName, setNewShapeName] = useState("");

  // UNDO / REDO STACKS
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const STACK_LIMIT = 20;

  const initializationRef = useRef(false);

  // Helper for deep cloning
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

  // Push current state to undo stack BEFORE change
  const pushToUndo = () => {
    setUndoStack(prev => {
      const newStack = [...prev, deepClone(mountingStructureItems)];
      if (newStack.length > STACK_LIMIT) return newStack.slice(1);
      return newStack;
    });
    setRedoStack([]); // Clear redo on new action
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previousState = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, deepClone(mountingStructureItems)]);
    setMountingStructureItems(previousState);
    setUndoStack(prev => prev.slice(0, prev.length - 1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[redoStack.length - 1];
    const currentState = deepClone(mountingStructureItems);

    setUndoStack(prev => [...prev, currentState]);
    setMountingStructureItems(nextState);
    setRedoStack(prev => prev.slice(0, prev.length - 1));
  };

  useEffect(() => {
    fetchMountingStructureCategories();
    fetchMakeOptions();
  }, []);

  const fetchMakeOptions = async () => {
    try {
      const res = await solisPowerApi.fetchActiveBrands();
      if (res) {
        setMakeOptions(res.map(b => ({
          label: b.name,
          value: b.id,
          make_type: b.make_type,
          section_type: b.section_type
        })));
      }
    } catch (err) {
      console.error("Error fetching brands:", err);
    }
  };


  // ✅ STEP 3: FIX EDIT CASE (REPLICATED FROM BOM)
  // Automatically normalize items with dropdown values when labels are found
  useEffect(() => {
    if (mountingStructureItems.sections?.length > 0) {
      const needsUpdate = mountingStructureItems.sections.some(section =>
        (section.parts || []).some(part => (Array.isArray(part.make) && part.make.length > 0 && !part.makeDropdown))
      );

      if (needsUpdate) {
        setMountingStructureItems(prev => ({
          ...prev,
          sections: (prev.sections || []).map(section => ({
            ...section,
            parts: (section.parts || []).map(part => {
              if (Array.isArray(part.make) && part.make.length > 0 && !part.makeDropdown) {
                return {
                  ...part,
                  makeDropdown: part.make.map(label => ({ label, value: label }))
                };
              }
              return part;
            })
          }))
        }));
      }
    }
  }, [mountingStructureItems.sections?.length, makeOptions.length]);

  // Auto-load template for new quotations when tab is clicked
  useEffect(() => {
    if (activeTab === "mounting" && !quotationId) {
      if (!mountingStructureItems.sections || mountingStructureItems.sections.length === 0) {
        initializeFromTemplate();
      }
    }
  }, [activeTab, quotationId]);

  const fetchMountingStructureCategories = async () => {
    try {
      const res = await solisPowerApi.fetchActiveBomCategories();
      if (res) {
        setBomCategoryOptions(res.filter(c => c.bom_type === 'MOUNTING_STRUCTURE').map(c => ({ value: c.id, label: c.category_name })));
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const initializeFromTemplate = async () => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    try {
      let makes = makeOptions;
      if (makes.length === 0) {
        const res = await solisPowerApi.fetchActiveBrands();
        if (res) {
          console.log("makes", res);
          makes = res.map(b => ({
            label: b.name,
            value: b.id,
            make_type: b.make_type,
            section_type: b.section_type
          }));
          setMakeOptions(makes);
        }
      }

      const template = deepClone(PREDEFINED_BOM.module_mounting_structure);
      if (!template || !template.sections) return;

      const mappedSections = template.sections.map((section, index) => {
        const item = {
          ...section,
          id: uuidv4(),
          section_uuid: uuidv4(),
          s_no: index + 1,
          status: "Active"
        };

        if (item.parts) {
          item.parts = item.parts.map(part => {
            if (part.make && Array.isArray(part.make)) {
              // Store labels directly (BOM pattern)
              part.make = part.make.map(label => label.toUpperCase());
              // Populate dropdown UI binding
              part.makeDropdown = part.make.map(label => ({ label, value: label }));
            }
            return part;
          });
        }
        return item;
      });

      setMountingStructureItems(prev => ({
        ...prev,
        section_name: template.section_name || "MODULE MOUNTING STRUCTURE",
        sections: mappedSections
      }));
    } catch (error) {
      console.error("Error initializing mounting structure template:", error);
    }
  };

  const handleReset = async () => {
    pushToUndo();
    initializationRef.current = false;
    await initializeFromTemplate();
    PubSub.publish("RECORD_SAVED_TOAST", { title: "Success", message: "Reset to template successful" });
  };

  const deleteSection = async (index) => {
    const section = sections[index];
    if (window.confirm(`Are you sure you want to delete "${section.section_name || section.roof_type}"?`)) {
      try {
        // If section has a real ID (not temp), delete from database
        if (section.id && !String(section.id).startsWith('temp_')) {
          await solisPowerApi.deleteMountingStructure(section.id);
        }

        pushToUndo();
        setMountingStructureItems(prev => {
          const updatedSections = [...(prev.sections || [])].filter((_, i) => i !== index);
          return { ...prev, sections: updatedSections };
        });
      } catch (err) {
        console.error("Error deleting mounting section:", err);
        alert("Failed to delete section. Please try again.");
      }
    }
  };

  const createEmptyPart = () => ({
    id: null,
    item_name: "",
    specification: "",
    size: "",
    shape: "",
    make: [],
    special_structure_type: "",
    special_structure_length: null,
    input_type: "ROW"
  });

  const createEmptyComboPart = () => ({
    id: null,
    s_no: null,
    item_name: "",
    special_structure_type: "",
    specification: "",
    special_structure_length: null,
    roof_type: "RCC",
    no_of_panels: null
  });

  const addManualRow = () => {
    pushToUndo();
    const newSection = {
      id: uuidv4(),
      section_uuid: uuidv4(),
      roof_type: "RCC",
      section_name: "NEW SECTION",
      structure_height_front: null,
      structure_height_back: null,
      parts: [createEmptyPart()],
      status: "Active"
    };
    setMountingStructureItems(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
  };

  const addPartRow = (sectionIdx) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (updatedSections[sectionIdx]) {
        const updatedParts = [...(updatedSections[sectionIdx].parts || []), createEmptyPart()];
        updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], parts: updatedParts };
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const addComboPartRow = (sectionIdx) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (updatedSections[sectionIdx]) {
        const updatedParts = [...(updatedSections[sectionIdx].parts || []), createEmptyComboPart()];
        updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], parts: updatedParts };
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const deletePartRow = (sectionIdx, partIdx, rowId) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      pushToUndo();
      setMountingStructureItems(prev => {
        const updatedSections = [...(prev.sections || [])];
        if (updatedSections[sectionIdx]) {
          const updatedParts = (updatedSections[sectionIdx].parts || []).filter((_, i) => i !== partIdx);
          updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], parts: updatedParts };
        }
        return { ...prev, sections: updatedSections };
      });
    }
  };

  const handleAddNewShape = () => {
    if (!newShapeName.trim()) return;
    const newVal = newShapeName.trim().toUpperCase();
    if (!shapeOptions.find(o => o.value === newVal)) {
      setShapeOptions([...shapeOptions, { value: newVal, label: newVal }]);
    }
    setNewShapeName("");
    setShowAddShapePrompt(false);
  };

  const handleStrictFieldChange = (sectionIdx, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (updatedSections[sectionIdx]) {
        updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], [key]: val };
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const handlePartFieldChange = (sectionIdx, partIdx, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (!updatedSections[sectionIdx]) return prev;

      const updatedParts = [...(updatedSections[sectionIdx].parts || [])];
      if (!updatedParts[partIdx]) return prev;

      let processedVal = val;
      if (key === 'make') {
        const selectedOptions = Array.isArray(val) ? val : [];

        updatedParts[partIdx].make = selectedOptions.map(opt => opt.label);
        updatedParts[partIdx].makeDropdown = selectedOptions;

        console.log('[MS] handleMakeChange (Labels):', updatedParts[partIdx].make);
        processedVal = updatedParts[partIdx].make; // for the spread below
      } else if (key === 'shape') {
        processedVal = val?.value || val;
      }

      updatedParts[partIdx] = { ...updatedParts[partIdx], [key]: processedVal };
      updatedSections[sectionIdx] = { ...updatedSections[sectionIdx], parts: updatedParts };

      return { ...prev, sections: updatedSections };
    });
  };

  const handleComboFieldChange = (sectionIdx, partIdx, key, val) => {
    pushToUndo();
    setMountingStructureItems(prev => {
      const updatedSections = [...(prev.sections || [])];
      if (!updatedSections[sectionIdx]) return prev;

      updatedSections[sectionIdx] = {
        ...updatedSections[sectionIdx],
        parts: (updatedSections[sectionIdx].parts || []).map((part, i) =>
          i === partIdx ? { ...part, [key]: val } : part
        )
      };

      return { ...prev, sections: updatedSections };
    });
  };

  const sections = mountingStructureItems.sections || [];

  return (
    <div className="mt-4">
      {/* Header with Undo/Redo/Reset */}
      <Row className="mb-3 align-items-center">
        <Col lg={6}>
          <h5 className="fw-bold mb-0" style={{ color: "#364794" }}>Module Mounting Structure</h5>
          <small className="text-muted">
            {quotationId ? "Editing existing structure" : "New structure from template"}
          </small>
        </Col>
        <Col lg={6} className="text-end d-flex gap-2 justify-content-end align-items-center">
          <div className="btn-group me-2 shadow-sm">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={undoStack.length === 0}
              onClick={handleUndo}
              title="Undo (Ctrl+Z)"
            >
              <i className="fa fa-undo"></i>
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={redoStack.length === 0}
              onClick={handleRedo}
              title="Redo (Ctrl+Y)"
            >
              <i className="fa fa-repeat"></i>
            </Button>
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            className="shadow-sm"
            onClick={handleReset}
          >
            <i className="fa fa-refresh me-1"></i> Reset
          </Button>
          <Button size="sm" className="btn-create shadow-sm" onClick={addManualRow}>
            <i className="fa fa-plus me-1"></i> Add Roof Type
          </Button>
        </Col>
      </Row>

      {sections.length === 0 ? (
        <Card className="text-center py-5 shadow-sm border-0 bg-light">
          <Card.Body>
            <i className="fa fa-building fa-3x text-muted mb-3"></i>
            <h6 className="text-muted">No sections available</h6>
            <Button size="sm" className="btn-create mt-2" onClick={handleReset}>
              Load Template
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Accordion alwaysOpen defaultActiveKey={sections.map((_, i) => i.toString())} className="bom-accordion shadow-sm rounded overflow-hidden border-0">
          {/* 1. RENDER NORMAL SECTIONS (Filter out Special Structure) */}
          {sections.map((section, sIdx) => {
            const isSpecial = section.section_name === "SPECIAL STRUCTURE" || section.roof_type === "SPECIAL STRUCTURE";
            if (isSpecial) return null; // Skip in main loop

            const currentParts = section.parts || [];
            const isRCCorGround = section.roof_type === "RCC" || section.roof_type === "GROUND";
            const isTinShed = section.roof_type === "TIN SHED";
            const isCombo = section.roof_type === "COMBO";

            return (
              <Accordion.Item key={section.section_uuid || section.id || sIdx} eventKey={sIdx.toString()} className="border-0 bg-white mb-2 overflow-hidden shadow-sm">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <div
                      className="flex-fill me-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Form.Control
                        size="sm"
                        value={section.section_name || section.roof_type || ""}
                        className="fw-bold border-0 bg-transparent p-0"
                        style={{ color: "#364794", fontSize: "1rem" }}
                        onChange={(e) => handleStrictFieldChange(sIdx, "section_name", e.target.value)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <i
                        className="fa fa-trash text-danger opacity-75 hover-opacity-100 cursor-pointer"
                        title="Delete Section"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Are you sure you want to delete this section?")) {
                            deleteSection(sIdx);
                          }
                        }}
                      ></i>
                      <i className="fa fa-chevron-down small text-muted opacity-50"></i>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-4">
                  {/* RCC / GROUND LAYOUT */}
                  {isRCCorGround && (
                    <>
                      <Row className="mb-4 g-3 align-items-end">
                        <Col lg={3}>
                          <Form.Group>
                            <Form.Label className="form-view-label mb-1">FRONT HEIGHT (FT)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              placeholder="0.00"
                              value={section.structure_height_front ?? ""}
                              onChange={(e) => handleStrictFieldChange(sIdx, "structure_height_front", e.target.value)}
                              className="border-secondary-subtle shadow-sm"
                            />
                          </Form.Group>
                        </Col>
                        <Col lg={3}>
                          <Form.Group>
                            <Form.Label className="form-view-label mb-1">BACK HEIGHT (FT)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              placeholder="0.00"
                              value={section.structure_height_back ?? ""}
                              onChange={(e) => handleStrictFieldChange(sIdx, "structure_height_back", e.target.value)}
                              className="border-secondary-subtle shadow-sm"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table bordered hover size="sm" className="mb-0 shadow-sm border-0">
                          <thead className="bg-light align-middle text-uppercase small" style={{ borderBottom: "2px solid #364794" }}>
                            <tr>
                              <th style={{ width: "20%" }}>Name</th>
                              <th style={{ width: "15%" }}>Specs</th>
                              <th style={{ width: "15%" }}>Size</th>
                              <th style={{ width: "20%" }}>Shape</th>
                              <th style={{ width: "25%" }}>Make (Master)</th>
                            </tr>
                          </thead>
                          <tbody className="border-0">
                            {currentParts.map((part, pIdx) => {
                              const isOthers = part.item_name === "OTHERS";

                              if (isOthers) {
                                return (
                                  <OthersRow
                                    key={part.id || pIdx}
                                    part={part}
                                    sIdx={sIdx}
                                    pIdx={pIdx}
                                    handlePartFieldChange={handlePartFieldChange}
                                  />
                                );
                              }

                              // Filter make options by section_type="Mounting Structure" and make_type=roof_type
                              const targetRoofType = (section.roof_type || "").toUpperCase();
                              const sectionFilteredMakes = makeOptions.filter(opt =>
                                opt.section_type === "Mounting Structure" &&
                                (opt.make_type || "").toUpperCase() === targetRoofType
                              );

                              return (
                                <PartRow
                                  key={part.id || pIdx}
                                  part={part}
                                  sIdx={sIdx}
                                  pIdx={pIdx}
                                  shapeOptions={shapeOptions}
                                  makeOptions={sectionFilteredMakes}
                                  handlePartFieldChange={handlePartFieldChange}
                                  setShowAddShapePrompt={setShowAddShapePrompt}
                                  deletePartRow={deletePartRow}
                                />
                              );
                            })}
                          </tbody>
                        </Table>
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => addPartRow(sIdx)}
                            className="border-dashed"
                          >
                            <i className="fa fa-plus me-1"></i> Add Row
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* TIN SHED LAYOUT */}
                  {isTinShed && (
                    <Row className="g-4">
                      <Col lg={6}>
                        <Form.Label className="form-view-label mb-1 text-uppercase small fw-bold">Structure Type</Form.Label>
                        <Select
                          isMulti
                          isSearchable={true}
                          closeMenuOnSelect={false}
                          options={(section.structure_type || []).map(t => ({ value: t, label: t }))}
                          value={(Array.isArray(section.tin_shed_structure_type)
                            ? section.tin_shed_structure_type
                            : (section.tin_shed_structure_type ? [section.tin_shed_structure_type] : [])
                          ).map(t => ({ value: t, label: t }))}
                          onChange={(sel) => handleStrictFieldChange(sIdx, "tin_shed_structure_type", sel ? sel.map(o => o.value) : [])}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (base) => ({ ...base, minHeight: "38px", border: "1px solid #ced4da", borderRadius: "6px" }),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </Col>
                      <Col lg={6}>
                        <Form.Label className="form-view-label mb-1 text-uppercase small fw-bold">Mounting Type</Form.Label>
                        <Select
                          isMulti
                          isSearchable={true}
                          closeMenuOnSelect={false}
                          options={(section.mounting_type || []).map(t => ({ value: t, label: t }))}
                          value={(Array.isArray(section.tin_shed_mounting_type)
                            ? section.tin_shed_mounting_type
                            : (section.tin_shed_mounting_type ? [section.tin_shed_mounting_type] : [])
                          ).map(t => ({ value: t, label: t }))}
                          onChange={(sel) => handleStrictFieldChange(sIdx, "tin_shed_mounting_type", sel ? sel.map(o => o.value) : [])}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (base) => ({ ...base, minHeight: "38px", border: "1px solid #ced4da", borderRadius: "6px" }),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </Col>
                    </Row>
                  )}

                  {/* COMBO LAYOUT */}
                  {isCombo && (
                    <div className="table-responsive">
                      <Table bordered hover size="sm" className="mb-0 shadow-sm border-0">
                        <thead className="bg-light align-middle text-uppercase small" style={{ borderBottom: "2px solid #364794" }}>
                          <tr>
                            <th className="text-center" style={{ width: "10%" }}>S No.</th>
                            <th style={{ width: "45%" }}>Roof Type</th>
                            <th style={{ width: "45%" }}>No. of Panels</th>
                          </tr>
                        </thead>
                        <tbody className="border-0">
                          {currentParts.map((pi, piIdx) => (
                            <ComboRow
                              key={pi.id || piIdx}
                              pi={pi}
                              sIdx={sIdx}
                              piIdx={piIdx}
                              handleComboFieldChange={handleComboFieldChange}
                              deletePartRow={deletePartRow}
                            />
                          ))}
                        </tbody>
                      </Table>
                      <div className="mt-2 text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => addComboPartRow(sIdx)}
                          className="border-dashed"
                        >
                          <i className="fa fa-plus me-1"></i> Add Item
                        </Button>
                      </div>
                      {section.note && (
                        <div className="mt-3 p-2 bg-light rounded small text-muted border-start border-primary border-4">
                          <strong>NOTE:</strong> {section.note}
                        </div>
                      )}
                    </div>
                  )}

                  {/* SPECIAL STRUCTURE LAYOUT */}
                  {isSpecial && (
                    <div className="table-responsive">
                      <Table bordered hover size="sm" className="mb-0 shadow-sm border-0">
                        <thead className="bg-light align-middle text-uppercase small" style={{ borderBottom: "2px solid #364794" }}>
                          <tr>
                            <th style={{ width: "25%" }}>Name</th>
                            <th style={{ width: "25%" }}>Type</th>
                            <th style={{ width: "30%" }}>Specification</th>
                            <th style={{ width: "20%" }}>Length (FT)</th>
                          </tr>
                        </thead>
                        <tbody className="border-0">
                          {currentParts.map((part, pIdx) => {
                            const isTextBox = part.input_type === "TEXT_BOX";
                            if (isTextBox) {
                              return (
                                <tr key={pIdx}>
                                  <td className="fw-bold bg-light align-middle">{part.item_name}</td>
                                  <td colSpan="3">
                                    <EditableCell
                                      placeholder={`Enter ${part.item_name.toLowerCase()} details...`}
                                      value={part.specification || ""}
                                      className="border-0 bg-white"
                                      onChange={(val) => handlePartFieldChange(sIdx, pIdx, "specification", val)}
                                    />
                                  </td>
                                </tr>
                              );
                            }
                            return (
                              <tr key={pIdx}>
                                <td className="fw-bold bg-light align-middle">{part.item_name}</td>
                                <td>
                                  <EditableCell
                                    value={part.special_structure_type || part.shape || ""}
                                    className="border-0 bg-white"
                                    onChange={(val) => {
                                      handlePartFieldChange(sIdx, pIdx, "special_structure_type", val);
                                      handlePartFieldChange(sIdx, pIdx, "shape", val);
                                    }}
                                  />
                                </td>
                                <td>
                                  <EditableCell
                                    value={part.special_structure_specs || ""}
                                    className="border-0 bg-white"
                                    onChange={(val) => handlePartFieldChange(sIdx, pIdx, "special_structure_specs", val)}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <EditableCell
                                      type="number"
                                      value={part.special_structure_length || part.size || ""}
                                      className="border-0 bg-white shadow-sm flex-fill"
                                      onChange={(val) => {
                                        handlePartFieldChange(sIdx, pIdx, "special_structure_length", val);
                                        handlePartFieldChange(sIdx, pIdx, "size", val);
                                      }}
                                    />
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="text-danger p-0 border-0"
                                      onClick={() => deletePartRow(sIdx, pIdx, part.id)}
                                    >
                                      <i className="fa fa-trash"></i>
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div className="mt-2 text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            pushToUndo();
                            setMountingStructureItems(prev => {
                              const updatedSections = [...prev.sections];
                              updatedSections[sIdx].parts = [...(updatedSections[sIdx].parts || []), {
                                id: null,
                                item_name: "NEW ITEM",
                                special_structure_type: "",
                                specification: "",
                                special_structure_length: null
                              }];
                              return { ...prev, sections: updatedSections };
                            });
                          }}
                          className="border-dashed"
                        >
                          <i className="fa fa-plus me-1"></i> Add Special Item
                        </Button>
                      </div>
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}

          {/* 2. RENDER SPECIAL STRUCTURE SEPARATELY (Render only once) */}
          {sections.map((section, sIdx) => {
            const isSpecial = section.section_name === "SPECIAL STRUCTURE" || section.roof_type === "SPECIAL STRUCTURE";
            if (!isSpecial) return null; // Show only special

            const currentParts = section.parts || [];

            return (
              <Accordion.Item key="special_structure_item" eventKey="special" className="border-0 bg-white mb-2 overflow-hidden shadow-sm">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <div className="flex-fill me-3" onClick={(e) => e.stopPropagation()}>
                      <Form.Control
                        size="sm"
                        value={section.section_name}
                        className="fw-bold border-0 bg-transparent p-0"
                        style={{ color: "#364794", fontSize: "1rem" }}
                        readOnly
                      />
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <i
                        className="fa fa-trash text-danger opacity-75 hover-opacity-100 cursor-pointer"
                        title="Delete Section"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Are you sure you want to delete this section?")) {
                            deleteSection(sIdx);
                          }
                        }}
                      ></i>
                      <i className="fa fa-chevron-down small text-muted opacity-50"></i>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-4">
                  <div className="table-responsive">
                    <Table bordered hover size="sm" className="mb-0 shadow-sm border-0">
                      <thead className="bg-light align-middle text-uppercase small" style={{ borderBottom: "2px solid #364794" }}>
                        <tr>
                          <th style={{ width: "25%" }}>Name</th>
                          <th style={{ width: "25%" }}>Type</th>
                          <th style={{ width: "30%" }}>Specification</th>
                          <th style={{ width: "20%" }}>Length (FT)</th>
                        </tr>
                      </thead>
                      <tbody className="border-0">
                        {currentParts.map((part, pIdx) => {
                          const isTextBox = part.input_type === "TEXT_BOX";
                          if (isTextBox) {
                            return (
                              <tr key={pIdx}>
                                <td className="fw-bold bg-light align-middle">{part.item_name}</td>
                                <td colSpan="3">
                                  <EditableCell
                                    placeholder={`Enter ${part.item_name.toLowerCase()} details...`}
                                    value={part.special_structure_specs || part.specification || ""}
                                    className="border-0 bg-white"
                                    onChange={(val) => {
                                      handlePartFieldChange(sIdx, pIdx, "special_structure_specs", val);
                                      handlePartFieldChange(sIdx, pIdx, "specification", val);
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          }
                          return (
                            <tr key={pIdx}>
                              <td className="fw-bold bg-light align-middle">{part.item_name}</td>
                              <td>
                                <EditableCell
                                  value={part.special_structure_type || part.shape || ""}
                                  className="border-0 bg-white"
                                  onChange={(val) => {
                                    handlePartFieldChange(sIdx, pIdx, "special_structure_type", val);
                                    handlePartFieldChange(sIdx, pIdx, "shape", val);
                                  }}
                                />
                              </td>
                              <td>
                                <EditableCell
                                  value={part.special_structure_specs || part.specification || ""}
                                  className="border-0 bg-white"
                                  onChange={(val) => {
                                    handlePartFieldChange(sIdx, pIdx, "special_structure_specs", val);
                                    handlePartFieldChange(sIdx, pIdx, "specification", val);
                                  }}
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <EditableCell
                                    type="number"
                                    value={part.special_structure_length || part.size || ""}
                                    className="border-0 bg-white shadow-sm flex-fill"
                                    onChange={(val) => {
                                      handlePartFieldChange(sIdx, pIdx, "special_structure_length", val);
                                      handlePartFieldChange(sIdx, pIdx, "size", val);
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="text-danger p-0 border-0"
                                    onClick={() => deletePartRow(sIdx, pIdx, part.id)}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <div className="mt-2 text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          pushToUndo();
                          setMountingStructureItems(prev => {
                            const updatedSections = [...prev.sections];
                            updatedSections[sIdx].parts = [...(updatedSections[sIdx].parts || []), {
                              id: null,
                              item_name: "NEW ITEM",
                              special_structure_type: "",
                              specification: "",
                              special_structure_length: null,
                              input_type: "ROW"
                            }];
                            return { ...prev, sections: updatedSections };
                          });
                        }}
                        className="border-dashed"
                      >
                        <i className="fa fa-plus me-1"></i> Add Special Item
                      </Button>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {/* Add Shape Modal Prompt */}
      {showAddShapePrompt && (
        <div className="modal-backdrop-custom d-flex align-items-center justify-content-center" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10000 }}>
          <Card className="shadow shadow-lg border-0" style={{ width: "350px" }}>
            <Card.Body className="p-4 text-center">
              <h6 className="fw-bold mb-3">Add New Shape Option</h6>
              <Form.Control
                autoFocus
                placeholder="Ex. HEXAGONAL..."
                value={newShapeName}
                onChange={(e) => setNewShapeName(e.target.value)}
                className="mb-3"
              />
              <div className="d-flex gap-2 justify-content-center">
                <Button variant="outline-secondary" size="sm" onClick={() => setShowAddShapePrompt(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleAddNewShape}>Add Shape</Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default QuotationMountingStructures;
